import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  DocumentTextIcon, 
  TrashIcon, 
  EyeIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  CalendarIcon,
  FolderIcon,
  PencilIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon  // Changed from TrendingUpIcon to ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

function ViewCompliance() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Create axios instance with REACT_APP_API_URL
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 30000,
  });

  // Add token interceptor
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const HandleAddCompliance = () => {
    window.location.href = '/admin/addcompliance';
  };

  // Fetch compliance files
  const fetchComplianceFiles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/viewcompliance');
      if (response.data && response.data.data) {
        setFiles(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setFiles(response.data);
      } else {
        setFiles([]);
      }
    } catch (error) {
      Swal.fire(
        'Error',
        error.response?.data?.message || error.message || 'Failed to load compliance files.',
        'error'
      );
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplianceFiles();
  }, []);

  // Delete compliance file
  const deleteComplianceFile = async (id, title) => {
    const confirm = await Swal.fire({
      title: 'Delete Compliance File?',
      text: `Are you sure you want to delete "${title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#fff',
      customClass: {
        popup: 'rounded-xl',
        title: 'text-lg font-bold',
        confirmButton: 'bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg'
      }
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.delete(`/viewcompliance/${id}`);
      Swal.fire({
        title: 'Deleted!',
        text: 'Compliance file has been deleted successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        background: '#fff',
        customClass: {
          popup: 'rounded-xl'
        }
      });
      await fetchComplianceFiles();
    } catch (error) {
      Swal.fire(
        'Error',
        error.response?.data?.message || error.message || 'Failed to delete compliance file.',
        'error'
      );
    }
  };

  // Update compliance file (edit)
  const updateComplianceFile = async (file) => {
    const { value: formValues } = await Swal.fire({
      title: 'Edit Compliance File',
      html: `
        <input id="swal-title" class="swal2-input" placeholder="Title" value="${file.title || ''}">
        <textarea id="swal-description" class="swal2-textarea" placeholder="Description">${file.description || ''}</textarea>
        <select id="swal-status" class="swal2-select">
          <option value="approved" ${file.status === 'approved' ? 'selected' : ''}>Approved</option>
          <option value="pending" ${file.status === 'pending' ? 'selected' : ''}>Pending</option>
          <option value="rejected" ${file.status === 'rejected' ? 'selected' : ''}>Rejected</option>
          <option value="under_review" ${file.status === 'under_review' ? 'selected' : ''}>Under Review</option>
        </select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg',
        cancelButton: 'bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg'
      },
      preConfirm: () => {
        const title = document.getElementById('swal-title').value;
        const description = document.getElementById('swal-description').value;
        const status = document.getElementById('swal-status').value;
        if (!title) {
          Swal.showValidationMessage('Title is required');
          return false;
        }
        return { title, description, status };
      }
    });

    if (formValues) {
      try {
        const response = await api.put(`/viewcompliance/${file.id}`, {
          title: formValues.title,
          description: formValues.description,
          status: formValues.status
        });
        
        Swal.fire({
          title: 'Updated!',
          text: 'Compliance file has been updated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          customClass: {
            popup: 'rounded-xl'
          }
        });
        await fetchComplianceFiles();
      } catch (error) {
        Swal.fire(
          'Error',
          error.response?.data?.message || 'Failed to update compliance file.',
          'error'
        );
      }
    }
  };

  // Get file icon based on extension
  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch(extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      case 'zip':
      case 'rar':
        return '📦';
      default:
        return '📁';
    }
  };

  // Get file type badge color
  const getFileTypeColor = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch(extension) {
      case 'pdf':
        return 'bg-red-100 text-red-700';
      case 'doc':
      case 'docx':
        return 'bg-blue-100 text-blue-700';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'bg-green-100 text-green-700';
      case 'zip':
      case 'rar':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'under_review':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4" />;
      case 'rejected':
        return <XCircleIcon className="h-4 w-4" />;
      case 'under_review':
        return <ArrowTrendingUpIcon className="h-4 w-4" />;  // Changed here
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  // Filter and sort files
  const filteredFiles = files.filter(file => 
    file.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedFiles = sortedFiles.slice(startIndex, startIndex + itemsPerPage);

  // Calculate comprehensive statistics
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  const stats = {
    total: files.length,
    today: files.filter(file => {
      const fileDate = new Date(file.created_at);
      fileDate.setHours(0, 0, 0, 0);
      return fileDate.getTime() === today.getTime();
    }).length,
    thisMonth: files.filter(file => {
      const fileDate = new Date(file.created_at);
      return fileDate.getMonth() === currentMonth && fileDate.getFullYear() === currentYear;
    }).length,
    approved: files.filter(file => file.status === 'approved').length,
    pending: files.filter(file => file.status === 'pending').length,
    rejected: files.filter(file => file.status === 'rejected').length,
    underReview: files.filter(file => file.status === 'under_review').length,
    recent: files.filter(file => {
      const daysAgo = (new Date() - new Date(file.created_at)) / (1000 * 60 * 60 * 24);
      return daysAgo <= 7;
    }).length,
    fileTypes: {
      pdf: files.filter(f => f.file_path?.toLowerCase().endsWith('.pdf')).length,
      documents: files.filter(f => /\.(doc|docx|txt)$/i.test(f.file_path)).length,
      images: files.filter(f => /\.(jpg|jpeg|png|gif)$/i.test(f.file_path)).length,
      archives: files.filter(f => /\.(zip|rar|7z)$/i.test(f.file_path)).length
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUpIcon className="h-4 w-4 opacity-30" />;
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="h-4 w-4" /> : 
      <ChevronDownIcon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-3 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading compliance files...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3">
                <ShieldCheckIcon className="h-10 w-10 text-indigo-600" />
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Compliance Management
                  </h1>
                  <p className="text-gray-600 mt-2">Manage and organize your compliance documents</p>
                </div>
              </div>
            </div>
            <button
              onClick={HandleAddCompliance}
              className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transform hover:scale-105 transition-all duration-200 flex items-center gap-2 shadow-lg"
            >
              <CloudArrowUpIcon className="h-5 w-5" />
              Add Compliance
            </button>
          </div>

          {/* Statistics Cards - Row 1 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-indigo-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Compliance</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.total}</p>
                </div>
                <div className="bg-indigo-100 rounded-full p-3">
                  <ShieldCheckIcon className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">This Month</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.thisMonth}</p>
                  <p className="text-xs text-blue-600 mt-1">{new Date().toLocaleString('default', { month: 'long' })} {currentYear}</p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <CalendarIcon className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Today</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.today}</p>
                  <p className="text-xs text-green-600 mt-1">{new Date().toLocaleDateString()}</p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <DocumentTextIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            
          </div>

          {/* Statistics Cards - Row 2 (Status Cards) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Approved</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.approved}</p>
                  <p className="text-xs text-green-600 mt-1">{((stats.approved / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Pending</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.pending}</p>
                  <p className="text-xs text-yellow-600 mt-1">{((stats.pending / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                </div>
                <div className="bg-yellow-100 rounded-full p-3">
                  <ClockIcon className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Rejected</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.rejected}</p>
                  <p className="text-xs text-red-600 mt-1">{((stats.rejected / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                </div>
                <div className="bg-red-100 rounded-full p-3">
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Under Review</p>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stats.underReview}</p>
                  <p className="text-xs text-blue-600 mt-1">{((stats.underReview / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <ArrowTrendingUpIcon className="h-6 w-6 text-blue-600" />  {/* Changed here */}
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search compliance files by title or description..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-3 pl-12 pr-4 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none bg-white"
              />
              <svg
                className="absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Compliance Files Table */}
        {paginatedFiles.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
              <ShieldCheckIcon className="h-10 w-10 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg font-medium">No compliance files found</p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm ? 'Try adjusting your search' : 'Add your first compliance document to get started'}
            </p>
            {!searchTerm && (
              <button
                onClick={HandleAddCompliance}
                className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Add Compliance
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-indigo-700">
                    <tr>
                      <th 
                        className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors"
                        onClick={() => handleSort('title')}
                      >
                        <div className="flex items-center gap-2">
                          Compliance Document
                          <SortIcon field="title" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Status
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors"
                        onClick={() => handleSort('description')}
                      >
                        <div className="flex items-center gap-2">
                          Description
                          <SortIcon field="description" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center gap-2">
                          Uploaded Date
                          <SortIcon field="created_at" />
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedFiles.map((file, index) => (
                      <tr key={file.id} className="hover:bg-indigo-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-2xl mr-3">
                              {getFileIcon(file.file_path)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {file.title || 'Untitled'}
                              </div>
                              <div className="text-xs text-gray-500">
                                {file.file_path?.split('/').pop() || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getFileTypeColor(file.file_path)}`}>
                            {file.file_path?.split('.').pop()?.toUpperCase() || 'FILE'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(file.status)}`}>
                            {getStatusIcon(file.status)}
                            {file.status ? file.status.replace('_', ' ').toUpperCase() : 'PENDING'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-xs truncate" title={file.description}>
                            {file.description || 'No description'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            {file.created_at ? new Date(file.created_at).toLocaleDateString() : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-400">
                            {file.created_at ? new Date(file.created_at).toLocaleTimeString() : ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {file.file_path && (
                              <a
                                href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/${file.file_path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 transition-colors p-1 hover:bg-indigo-100 rounded-lg"
                                title="View Compliance Document"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </a>
                            )}
                          
                           
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(startIndex + itemsPerPage, sortedFiles.length)}
                    </span>{' '}
                    of <span className="font-medium">{sortedFiles.length}</span> compliance files
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <div className="flex gap-1">
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 rounded-lg transition-colors ${
                              currentPage === pageNum
                                ? 'bg-indigo-600 text-white'
                                : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ViewCompliance;